import {TEAL_400} from '@wandb/weave/common/css/color.styles';
import React, {useEffect, useMemo, useState} from 'react';
import ReactFlow, {
  Controls,
  MarkerType,
  useReactFlow,
} from 'react-flow-renderer';

import {
  artifactMembershipNodeInputType,
  InfoDrawerArtifacts,
} from './InfoDrawerArtifacts';
import {InfoDrawerRuns, runNodeInputType} from './InfoDrawerRuns';
import {
  dagToFlowEls,
  getLayoutedElements,
  NODEHEIGHT,
  nodeType,
  NODEWIDTH,
} from './LayoutDagUtils';
import {Dag} from './utils';

interface LayoutFlowProps {
  dag: Dag;
  artifactID: string;
  expandedArtifact: string;
  expandedRun: string;
  updateDag: (dag: Dag, expandedArtifact: string, expandedRun: string) => void;
  filterGeneratedArtifacts: boolean;
  clusterSize?: number;
  directLineage: boolean;
}

export const LayoutFlow = (props: LayoutFlowProps) => {
  const {
    dag,
    artifactID,
    expandedArtifact,
    expandedRun,
    updateDag,
    filterGeneratedArtifacts,
    clusterSize,
    directLineage,
  } = props;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState<nodeType | undefined>(undefined);
  const [artifactMembershipNodeInput, setArtifactMembershipNodeInput] =
    useState<artifactMembershipNodeInputType | undefined>();
  const [runNodeInput, setRunNodeInput] = useState<
    runNodeInputType | undefined
  >();

  const onDrawerClose = () => {
    setIsDrawerOpen(false);
    setDrawerType(undefined);
    setArtifactMembershipNodeInput(undefined);
    setRunNodeInput(undefined);
  };

  const [dagNodes, clusterNodes, dagEdges] = useMemo(
    () =>
      dagToFlowEls(
        dag,
        artifactID,
        expandedArtifact,
        expandedRun,
        updateDag,
        setIsDrawerOpen,
        setDrawerType,
        setArtifactMembershipNodeInput,
        setRunNodeInput,
        filterGeneratedArtifacts,
        directLineage,
        clusterSize
      ),
    [
      dag,
      artifactID,
      expandedArtifact,
      expandedRun,
      updateDag,
      setIsDrawerOpen,
      setDrawerType,
      setArtifactMembershipNodeInput,
      setRunNodeInput,
      filterGeneratedArtifacts,
      directLineage,
      clusterSize,
    ]
  );

  const {
    nodes: layoutedNodes,
    edges: layoutedEdges,
    selectedNode,
    expandedNode,
  } = useMemo(
    () =>
      getLayoutedElements(
        dagNodes,
        clusterNodes,
        dagEdges,
        'LR',
        artifactID,
        expandedArtifact,
        expandedRun
      ),
    [
      dagNodes,
      clusterNodes,
      dagEdges,
      artifactID,
      expandedArtifact,
      expandedRun,
    ]
  );

  const {setCenter} = useReactFlow();

  const [elements, setElements] = useState(layoutedEdges);
  useEffect(() => {
    setElements(layoutedEdges);
  }, [layoutedEdges]);

  const onElementEnter = (event: React.MouseEvent, element: {id: string}) => {
    // Change edge color on hover
    setElements(prevElements =>
      prevElements.map(el =>
        el.id === element.id
          ? {
              ...el,
              zIndex: 5,
              style: {stroke: TEAL_400, strokeWidth: 1.5},
              markerEnd: {type: MarkerType.Arrow, color: TEAL_400},
            }
          : {
              ...el,
              zIndex: 0,
              style: {strokeWidth: 0.5},
            }
      )
    );
  };

  const onElementLeave = (event: React.MouseEvent, element: {id: string}) => {
    // Revert edge color when leaving hover
    setElements(prevElements =>
      prevElements.map(el => ({
        ...el,
        style: {stroke: '#b1b1b7', strokeWidth: 1},
        markerEnd: {type: MarkerType.Arrow, color: '#b1b1b7'},
      }))
    );
  };

  useEffect(() => {
    if (selectedNode != null) {
      setCenter(
        selectedNode.position.x + NODEWIDTH / 2,
        selectedNode.position.y + NODEHEIGHT,
        {
          zoom: 1,
          duration: 1000,
        }
      );
    }
  }, [selectedNode, setCenter]);

  return (
    <div
      className="layoutFlow"
      style={{
        position: 'relative',
        height: '100%',
        cursor: 'grab',
      }}>
      <ReactFlow
        onEdgeMouseLeave={onElementLeave}
        onEdgeMouseEnter={onElementEnter}
        minZoom={0.1}
        nodes={layoutedNodes}
        edges={elements}
        onNodesChange={(events: any) => {
          let move = true;
          events.forEach((e: any) => {
            if (e.type === 'select') {
              move = false;
            }
          });
          if (move && expandedNode != null) {
            setCenter(
              expandedNode.position.x + NODEWIDTH / 2,
              expandedNode.position.y + NODEHEIGHT,
              {
                zoom: 1,
                duration: 1000,
              }
            );
          } else if (move && selectedNode != null) {
            setCenter(
              selectedNode.position.x + NODEWIDTH / 2,
              selectedNode.position.y + NODEHEIGHT,
              {
                zoom: 1,
                duration: 1000,
              }
            );
          }
        }}
        nodesDraggable={false}
        onInit={() => {
          if (expandedNode != null) {
            setCenter(
              expandedNode.position.x + NODEWIDTH / 2,
              expandedNode.position.y + NODEHEIGHT,
              {
                zoom: 1,
                duration: 1000,
              }
            );
          } else if (selectedNode != null) {
            setCenter(
              selectedNode.position.x + NODEWIDTH / 2,
              selectedNode.position.y + NODEHEIGHT,
              {
                zoom: 1,
                duration: 1000,
              }
            );
          }
        }}
        fitView>
        <Controls style={{marginBottom: '50px'}} />
      </ReactFlow>
      {artifactMembershipNodeInput && drawerType === nodeType.ARTIFACT ? (
        <InfoDrawerArtifacts
          open={isDrawerOpen && drawerType === nodeType.ARTIFACT}
          closeDrawer={onDrawerClose}
          artifactMembershipNodeInput={artifactMembershipNodeInput}
        />
      ) : (
        runNodeInput && (
          <InfoDrawerRuns
            open={isDrawerOpen && drawerType === nodeType.RUN}
            closeDrawer={onDrawerClose}
            runNodeInput={runNodeInput}
          />
        )
      )}
    </div>
  );
};
